import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
    Building2, AlertCircle, Mail, Lock, User, Store,
    Globe, ChevronDown, ArrowRight, CheckCircle, ArrowLeft, Sparkles,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '@/lib/api-client';
import axios from 'axios';

// Fixed special character regex — matches backend
const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;

const registerSchema = z.object({
    companyName: z.string().min(2, 'Company name must be at least 2 characters.'),
    companySlug: z
        .string()
        .min(3, 'Slug must be at least 3 characters.')
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers, hyphens only.'),
    businessModel: z.enum(['single', 'multi']),
    businessType: z.enum(['retail', 'restaurant', 'wholesale', 'pharmacy', 'service']),
    adminFirstName: z.string().min(1, 'First name is required.'),
    adminLastName: z.string().min(1, 'Last name is required.'),
    adminEmail: z.string().email('Please enter a valid email address.'),
    adminPassword: z
        .string()
        .min(8, 'At least 8 characters required.')
        .regex(/[A-Z]/, 'Uppercase letter required.')
        .regex(/[a-z]/, 'Lowercase letter required.')
        .regex(/[0-9]/, 'Number required.')
        .regex(SPECIAL_CHAR_REGEX, 'Special character required.'),
});

type RegisterForm = z.infer<typeof registerSchema>;

const businessTypes = [
    { value: 'retail', label: 'Retail Store', emoji: '🏪' },
    { value: 'restaurant', label: 'Restaurant & Bar', emoji: '🍽️' },
    { value: 'wholesale', label: 'Wholesale Distribution', emoji: '📦' },
    { value: 'pharmacy', label: 'Pharmacy', emoji: '💊' },
    { value: 'service', label: 'Service Business', emoji: '🔧' },
];

export function RegisterPage() {
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const { setUser, setTenant } = useAuthStore();
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
        trigger,
    } = useForm<RegisterForm>({
        resolver: zodResolver(registerSchema),
        defaultValues: { businessModel: 'single', businessType: 'retail' },
    });

    const selectedModel = watch('businessModel');
    const password = watch('adminPassword') || '';

    // Updated: use the same regex as the schema
    const passedChecks = [
        password.length >= 8,
        /[A-Z]/.test(password),
        /[a-z]/.test(password),
        /[0-9]/.test(password),
        SPECIAL_CHAR_REGEX.test(password),
    ].filter(Boolean).length;

    const isLongEnough = password.length >= 8;

    const handleNextStep = async () => {
        const isValid = await trigger(['companyName', 'companySlug', 'businessModel', 'businessType']);
        if (isValid) setStep(2);
    };

    const onSubmit = async (data: RegisterForm) => {
        try {
            setError('');
            setIsLoading(true);

            const { data: resData } = await apiClient.post('/tenants/register', data);

            // Store tokens from registration response if returned
            const accessToken = resData.data?.accessToken || resData.data?.tokens?.accessToken;
            const refreshToken = resData.data?.refreshToken || resData.data?.tokens?.refreshToken;

            if (accessToken) localStorage.setItem('accessToken', accessToken);
            if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

            // Set user and tenant in store safely
            const userObj = resData.data?.user || resData.data?.adminUser;
            const tenantObj = resData.data?.tenant;

            if (userObj) {
                setUser(userObj, resData.data?.roles || [{ id: 'super-admin', name: 'Super Admin' }]);
            }
            if (tenantObj) {
                setTenant(tenantObj);
                if (tenantObj.id) {
                    localStorage.setItem('tenantId', tenantObj.id);
                }
            }

            // Extract valid tenantId from tenant, user, or JWT
            let tenantId = tenantObj?.id || userObj?.tenantId;
            if (!tenantId && accessToken) {
                try {
                    const payload = JSON.parse(atob(accessToken.split('.')[1]));
                    tenantId = payload?.tenantId;
                } catch {}
            }

            if (tenantId) {
                localStorage.setItem('tenantId', tenantId);
            }

            const email = data.adminEmail;

            // Route to subscription verification screen with valid tenantId
            if (tenantId) {
                navigate(`/verify-subscription?tenantId=${tenantId}&email=${encodeURIComponent(email)}`);
            } else {
                navigate(`/verify-subscription?email=${encodeURIComponent(email)}`);
            }
        } catch (err) {
            if (axios.isAxiosError(err)) {
                if (err.response?.status === 409) {
                    setError('This business name or slug already exists. Please choose another.');
                } else if (err.response?.status === 429) {
                    setError('Too many registration attempts. Please try again later.');
                } else if (!err.response) {
                    setError('No internet connection. Please check your network and try again.');
                } else {
                    setError(
                        err.response?.data?.error?.message || 'Registration failed. Please try again.'
                    );
                }
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl shadow-emerald-500/10">
                {/* Gradient Top Bar */}
                <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600" />

                <div className="px-8 pt-6 pb-2">
                    {/* Step Indicator */}
                    <div className="flex items-center justify-center gap-0 mb-6">
                        {[
                            { num: 1, label: 'Business Details', sub: 'Company info' },
                            { num: 2, label: 'Admin Account', sub: 'Your credentials' },
                        ].map((s, i, arr) => (
                            <div key={s.num} className="flex items-center flex-1">
                                <div className="flex flex-col items-center flex-1">
                                    <motion.div
                                        animate={{
                                            scale: step === s.num ? 1.1 : 1,
                                            backgroundColor: step >= s.num ? '#0D9488' : '#F1F5F9',
                                            borderColor: step >= s.num ? '#0D9488' : '#CBD5E1',
                                        }}
                                        className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center text-sm font-bold transition-all ${step >= s.num
                                                ? 'text-white shadow-lg shadow-emerald-500/20'
                                                : 'text-slate-400'
                                            }`}
                                    >
                                        {step > s.num ? <CheckCircle size={18} /> : s.num}
                                    </motion.div>
                                    <div className="text-center mt-2">
                                        <p
                                            className={`text-xs font-semibold ${step >= s.num ? 'text-emerald-600' : 'text-slate-400'
                                                }`}
                                        >
                                            {s.label}
                                        </p>
                                        <p className="text-[10px] text-slate-400">{s.sub}</p>
                                    </div>
                                </div>
                                {i < arr.length - 1 && (
                                    <div className="flex-1 -mt-6 px-2">
                                        <div
                                            className={`h-1 rounded-full transition-all duration-500 ${step > 1
                                                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-500'
                                                    : 'bg-slate-200'
                                                }`}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Header */}
                    <div className="flex items-center gap-3 mb-5">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 flex-shrink-0"
                        >
                            {step === 1 ? (
                                <Building2 size={20} className="text-white" />
                            ) : (
                                <User size={20} className="text-white" />
                            )}
                        </motion.div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900">
                                {step === 1 ? 'Create Your Business' : 'Set Up Admin Account'}
                            </h2>
                            <p className="text-xs text-slate-500">
                                {step === 1
                                    ? 'Tell us about your company'
                                    : 'Create your administrator credentials'}
                            </p>
                        </div>
                    </div>

                    {/* Error */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-2"
                            >
                                <AlertCircle size={16} className="text-rose-500 flex-shrink-0" />
                                <p className="text-xs text-rose-700">{error}</p>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Form */}
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <AnimatePresence mode="wait">
                            {step === 1 ? (
                                <motion.div
                                    key="step1"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="space-y-4"
                                >
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            label="Company Name"
                                            placeholder="My Store Ltd"
                                            error={errors.companyName?.message}
                                            leftIcon={<Store size={16} />}
                                            {...register('companyName')}
                                        />
                                        <Input
                                            label="URL Slug"
                                            placeholder="my-store"
                                            hint="my-store.nyan.app"
                                            error={errors.companySlug?.message}
                                            leftIcon={<Globe size={16} />}
                                            {...register('companySlug')}
                                        />
                                    </div>

                                    {/* Business Structure Selection */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-2 uppercase tracking-wider">
                                            Business Structure
                                        </label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setValue('businessModel', 'single')}
                                                className={`p-3.5 rounded-2xl border-2 text-left transition-all ${
                                                    selectedModel === 'single'
                                                        ? 'border-emerald-600 bg-emerald-50/80 shadow-sm shadow-emerald-500/10'
                                                        : 'border-slate-200 hover:border-slate-300 bg-white'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <span className="text-xl">🏪</span>
                                                    {selectedModel === 'single' && (
                                                        <CheckCircle size={16} className="text-emerald-600" />
                                                    )}
                                                </div>
                                                <p className="text-sm font-bold text-slate-900">Single Business</p>
                                                <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                                                    1 location & branch. Optimized for single shops, boutiques & cafes.
                                                </p>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => setValue('businessModel', 'multi')}
                                                className={`p-3.5 rounded-2xl border-2 text-left transition-all ${
                                                    selectedModel === 'multi'
                                                        ? 'border-emerald-600 bg-emerald-50/80 shadow-sm shadow-emerald-500/10'
                                                        : 'border-slate-200 hover:border-slate-300 bg-white'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <span className="text-xl">🏢</span>
                                                    {selectedModel === 'multi' && (
                                                        <CheckCircle size={16} className="text-emerald-600" />
                                                    )}
                                                </div>
                                                <p className="text-sm font-bold text-slate-900">Multi-Business</p>
                                                <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                                                    Multiple brands, subsidiaries & branches across locations.
                                                </p>
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
                                            Industry / Category
                                        </label>
                                        <div className="relative">
                                            <select
                                                {...register('businessType')}
                                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white text-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all appearance-none cursor-pointer hover:border-slate-300"
                                            >
                                                {businessTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.emoji} {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronDown
                                                size={16}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                                            />
                                        </div>
                                    </div>

                                    <Button
                                        type="button"
                                        onClick={handleNextStep}
                                        fullWidth
                                        size="lg"
                                        rightIcon={<ArrowRight size={18} />}
                                    >
                                        Continue to Admin Setup
                                    </Button>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="step2"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="space-y-4"
                                >
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            label="First Name"
                                            placeholder="John"
                                            error={errors.adminFirstName?.message}
                                            leftIcon={<User size={16} />}
                                            {...register('adminFirstName')}
                                        />
                                        <Input
                                            label="Last Name"
                                            placeholder="Doe"
                                            error={errors.adminLastName?.message}
                                            leftIcon={<User size={16} />}
                                            {...register('adminLastName')}
                                        />
                                    </div>
                                    <Input
                                        label="Admin Email"
                                        type="email"
                                        placeholder="admin@company.com"
                                        error={errors.adminEmail?.message}
                                        leftIcon={<Mail size={16} />}
                                        {...register('adminEmail')}
                                    />
                                    <Input
                                        label="Admin Password"
                                        type="password"
                                        placeholder="Create a strong password"
                                        error={errors.adminPassword?.message}
                                        isPassword
                                        leftIcon={<Lock size={16} />}
                                        {...register('adminPassword')}
                                    />

                                    {/* Password Strength */}
                                    {password.length > 0 && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="p-3 bg-slate-50 rounded-xl border border-slate-200"
                                        >
                                            <div className="flex items-center gap-1.5 mb-2">
                                                {[1, 2, 3, 4, 5].map((i) => (
                                                    <motion.div
                                                        key={i}
                                                        animate={{
                                                            backgroundColor:
                                                                i <= passedChecks ? '#10b981' : '#e2e8f0',
                                                        }}
                                                        className="flex-1 h-1.5 rounded-full"
                                                    />
                                                ))}
                                                <span
                                                    className={`text-[10px] font-bold ml-1 ${passedChecks >= 4
                                                            ? 'text-emerald-600'
                                                            : passedChecks >= 2
                                                                ? 'text-amber-600'
                                                                : 'text-rose-500'
                                                        }`}
                                                >
                                                    {passedChecks >= 4
                                                        ? 'Strong'
                                                        : passedChecks >= 2
                                                            ? 'Fair'
                                                            : 'Weak'}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-1">
                                                <CheckItem
                                                    passed={isLongEnough}
                                                    label="At least 8 characters"
                                                />
                                                <CheckItem
                                                    passed={/[A-Z]/.test(password)}
                                                    label="One uppercase letter"
                                                />
                                                <CheckItem
                                                    passed={/[a-z]/.test(password)}
                                                    label="One lowercase letter"
                                                />
                                                <CheckItem
                                                    passed={/[0-9]/.test(password)}
                                                    label="One number"
                                                />
                                                <CheckItem
                                                    passed={SPECIAL_CHAR_REGEX.test(password)}
                                                    label="One special character"
                                                />
                                            </div>
                                        </motion.div>
                                    )}

                                    <div className="flex gap-3 pt-1">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={() => setStep(1)}
                                            size="lg"
                                            leftIcon={<ArrowLeft size={18} />}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            type="submit"
                                            isLoading={isLoading}
                                            fullWidth
                                            size="lg"
                                            leftIcon={<Sparkles size={18} />}
                                        >
                                            Create Business
                                        </Button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </form>
                </div>

                {/* Footer */}
                <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 rounded-b-3xl">
                    <p className="text-sm text-slate-500 text-center">
                        Already have an account?{' '}
                        <Link
                            to="/login"
                            className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </motion.div>
    );
}

// Small helper component for password check items
function CheckItem({ passed, label }: { passed: boolean; label: string }) {
    return (
        <div
            className={`flex items-center gap-1.5 text-[10px] ${passed ? 'text-emerald-600' : 'text-slate-400'
                }`}
        >
            <div
                className={`w-1 h-1 rounded-full ${passed ? 'bg-emerald-500' : 'bg-slate-300'
                    }`}
            />
            {label}
        </div>
    );
}